import { useEffect, useRef, useState } from 'react'

import { ArrowRight01Icon } from '@hugeicons/react'
import clsx from 'clsx'

import { getPrimaryResourceType } from '@/shared/resourceModuleParity'
import { Badge } from '@/shared/ui/Badge'
import { Card } from '@/shared/ui/Card'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { ResourceModuleIcon } from '@/shared/ui/ResourceModuleIcon'
import { RewardPill } from '@/shared/ui/RewardPill'

import type { Goal } from '../model/milestones'
import { getLadderState } from '../model/milestones'
import {
  LADDER_COMPLETED_LABEL,
  ladderActiveMilestoneLabel,
  ladderActiveMilestonePercent,
  ladderAriaValueText,
  ladderRepeatLabel,
  ladderRepeatLabelShort,
  MILESTONE_ACHIEVED_LABEL
} from '../model/milestoneCopy'
import { LadderMilestoneBadge } from './LadderMilestoneBadge'

/** Quanto tempo o card fica em estado de conquista antes de reapontar. */
const ACHIEVEMENT_HOLD_MS = 6000

type Props = {
  goal: Extract<Goal, { kind: 'ladder' }>
  onPress?: () => void
}

/**
 * Card de uma escada de marcos.
 *
 * Progresso e recompensa refletem o próximo marco; o badge de repetição deixa
 * explícito quantas vezes a meta se repete; o 0/16 mostra marcos conquistados.
 */
export const MilestoneObjectiveCard = ({ goal, onPress }: Props) => {
  const state = getLadderState(goal.milestones)
  const sample = goal.milestones[0] ?? null
  const activeMilestone = state.nextMilestone ?? sample
  const resourceType = getPrimaryResourceType(sample?.objective_conditions ?? [])
  const coinReward = state.completed
    ? state.totalCoinReward
    : state.nextReward ?? 0

  const previousAchieved = useRef(state.achievedCount)
  const [justAchieved, setJustAchieved] = useState(false)

  useEffect(() => {
    if (state.achievedCount > previousAchieved.current) {
      setJustAchieved(true)
      const timer = setTimeout(() => setJustAchieved(false), ACHIEVEMENT_HOLD_MS)
      previousAchieved.current = state.achievedCount
      return () => clearTimeout(timer)
    }

    previousAchieved.current = state.achievedCount
    return undefined
  }, [state.achievedCount])

  const showAchievement = justAchieved && !state.completed
  const variant = state.completed ? 'success' : state.currentCount > 0 ? 'progress' : 'primary'

  return (
    <Card
      onClick={onPress}
      className={clsx('card-gamification cursor-pointer shadow-sm', !onPress && 'cursor-default')}
    >
      <Card.Body>
        <div className="flex items-start gap-2 sm:gap-3">
          <ResourceModuleIcon
            resourceType={resourceType}
            size={20}
            tileSize={44}
            completed={state.completed}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {(showAchievement || state.completed) && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="success" size="sm" showIcon={false}>
                  {state.completed ? LADDER_COMPLETED_LABEL : MILESTONE_ACHIEVED_LABEL}
                </Badge>
              </div>
            )}

            <Badge
              variant="success"
              tone="light"
              size="sm"
              showIcon={false}
              className="w-fit shrink-0 normal-case"
            >
              <span className="sm:hidden">{ladderRepeatLabelShort(state.totalMilestones)}</span>
              <span className="hidden sm:inline">
                {ladderRepeatLabel(state.totalMilestones)}
              </span>
            </Badge>

            <h3 className="m-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900">
              {goal.group.name}
            </h3>

            <ProgressBar.Row
              value={state.completed ? 100 : ladderActiveMilestonePercent(state)}
              variant={variant}
              size="sm"
              label={ladderActiveMilestoneLabel(state, activeMilestone)}
              aria-label={ladderAriaValueText(state, activeMilestone)}
            />
          </div>

          <div className="flex w-[70px] shrink-0 flex-col items-end gap-2 self-stretch">
            <ArrowRight01Icon
              size={16}
              variant="stroke"
              className="text-gray-600"
              aria-hidden
            />

            <div className="mt-auto flex flex-col items-end gap-1.5">
              <LadderMilestoneBadge
                achieved={state.achievedCount}
                total={state.totalMilestones}
                compact
              />
              <RewardPill type="coins" value={coinReward} />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
